import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import type { UseQueryResult } from "@tanstack/react-query";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { cn } from "../../lib/utils";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  useActivateMasterData,
  useCreateMasterData,
  useDeactivateMasterData,
  useUpdateMasterData,
  type MasterDataBody,
} from "./api";
import {
  DEFAULT_MASTER_DATA_VIEW,
  nextMasterDataSort,
  viewMasterDataRows,
  type MasterDataFormValues,
  type MasterDataKind,
  type MasterDataSort,
  type MasterDataStatusFilter,
  type MasterDataView,
  type SortValue,
} from "./helpers";

/** Sentinel Select value for "no selection" (Radix forbids an empty item value). */
const NONE = "__none__";

export interface FieldSpec {
  name: string;
  labelKey: string;
  type: "text" | "textarea" | "number" | "color" | "select";
  /** Options for a `select` field; the "none" choice is prepended automatically. */
  options?: { value: string; label: string }[];
  placeholderKey?: string;
}

export interface Column<Row> {
  key: string;
  headerKey: string;
  render: (row: Row) => ReactNode;
  className?: string;
  /** The column's comparable value; a column without one is not sortable (AC-071). */
  sortValue?: (row: Row) => SortValue;
  /** Counts and points in time sort largest / newest first on the first click. */
  descFirst?: boolean;
}

interface MasterDataRow {
  id: string;
  name: string;
  description?: string | null;
  active: boolean;
}

const STATUS_FILTERS: readonly MasterDataStatusFilter[] = ["all", "active", "inactive"];

const STATUS_FILTER_LABEL_KEY: Record<MasterDataStatusFilter, string> = {
  all: "adminMasterData.statusFilterOption.all",
  active: "adminMasterData.statusFilterOption.active",
  inactive: "adminMasterData.statusFilterOption.inactive",
};

export interface MasterDataSectionProps<Row extends MasterDataRow> {
  kind: MasterDataKind;
  query: UseQueryResult<Row[]>;
  columns: Column<Row>[];
  fields: FieldSpec[];
  /** Prefill the edit dialog from an existing row. */
  toFormValues: (row: Row) => MasterDataFormValues;
  /** Turn the dialog's string values into the API request body. */
  buildBody: (values: MasterDataFormValues) => MasterDataBody;
}

type DialogState<Row> =
  | { mode: "create" }
  | { mode: "edit"; row: Row }
  | null;

type PendingToggle<Row> =
  | { action: "deactivate"; row: Row }
  | { action: "activate"; row: Row }
  | null;

function useMasterDataErrors(): ErrorMessages {
  const { t } = useTranslation();
  return {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.generic"),
  };
}

/**
 * One kind's management table (F-009 / DO-011): all rows incl. inactive
 * (greyed + status badge), search over name and description, a status filter
 * and sortable columns (AC-071), create / edit via one dialog, and deactivate /
 * reactivate through the destructive confirmation pattern (IM-150).
 * Admin-only display; the server re-checks every write (ADR-API-09). All
 * errors surface the server's German RFC-7807 detail.
 */
export function MasterDataSection<Row extends MasterDataRow>({
  kind,
  query,
  columns,
  fields,
  toFormValues,
  buildBody,
}: MasterDataSectionProps<Row>): JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<MasterDataView>(DEFAULT_MASTER_DATA_VIEW);
  const [dialog, setDialog] = useState<DialogState<Row>>(null);
  const [pending, setPending] = useState<PendingToggle<Row>>(null);

  const allRows = query.data;
  const sortValues = useMemo(() => {
    const out: Record<string, (row: Row) => SortValue> = {};
    for (const column of columns) {
      if (column.sortValue) out[column.key] = column.sortValue;
    }
    return out;
  }, [columns]);
  const rows = useMemo(
    () => viewMasterDataRows(allRows ?? [], view, sortValues),
    [allRows, view, sortValues],
  );
  const inactiveCount = (allRows ?? []).filter((row) => !row.active).length;
  const narrowed = view.q.trim() !== "" || view.status !== "all";

  const colSpan = columns.length + 2; // + Status + Aktionen

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div
          role="group"
          aria-label={t("adminMasterData.listControls")}
          data-testid={`admin-master-data_controls_${kind}`}
          className="flex w-full flex-wrap items-end gap-3 sm:w-auto"
        >
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              data-testid={`admin-master-data_search_${kind}`}
              value={view.q}
              onChange={(event) =>
                setView((current) => ({ ...current, q: event.target.value }))
              }
              placeholder={t("adminMasterData.searchPlaceholder")}
              aria-label={t("adminMasterData.search")}
              className="pl-8"
            />
          </div>
          <Select
            value={view.status}
            onValueChange={(next) =>
              setView((current) => ({
                ...current,
                status: next as MasterDataStatusFilter,
              }))
            }
          >
            <SelectTrigger
              data-testid={`admin-master-data_status-filter_${kind}`}
              aria-label={t("adminMasterData.statusFilter")}
              className="w-full sm:w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(STATUS_FILTER_LABEL_KEY[status])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {inactiveCount > 0 ? (
            <span className="pb-2 text-xs text-muted-foreground">
              {t("adminMasterData.inactiveCount", { n: inactiveCount })}
            </span>
          ) : null}
        </div>
        <Button
          size="sm"
          data-testid={`admin-master-data_create_${kind}`}
          onClick={() => setDialog({ mode: "create" })}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t("adminMasterData.create")}
        </Button>
      </div>

      {query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{t("errors.load")}</span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : query.isLoading || !query.data ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-12 w-full animate-pulse rounded-md bg-muted"
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table data-testid={`admin-master-data_table_${kind}`}>
            <TableHeader>
              <TableRow>
                {columns.map((column) =>
                  column.sortValue ? (
                    <SortableHead
                      key={column.key}
                      column={column}
                      sort={view.sort}
                      onSort={(sort) => setView((current) => ({ ...current, sort }))}
                    />
                  ) : (
                    <TableHead key={column.key} className={column.className}>
                      {t(column.headerKey)}
                    </TableHead>
                  ),
                )}
                <TableHead>{t("adminMasterData.colStatus")}</TableHead>
                <TableHead className="w-48">
                  {t("adminMasterData.colActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={colSpan}
                    data-testid={`admin-master-data_empty_${kind}`}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {narrowed && (allRows?.length ?? 0) > 0
                      ? t("adminMasterData.noMatch")
                      : t("adminMasterData.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`admin-master-data_row_${row.id}`}
                    className={cn(!row.active && "bg-muted/50 text-muted-foreground")}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.key} className={column.className}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <StatusBadge active={row.active} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => setDialog({ mode: "edit", row })}
                        >
                          {t("adminMasterData.edit")}
                        </Button>
                        {row.active ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-destructive-text hover:text-destructive-text"
                            onClick={() =>
                              setPending({ action: "deactivate", row })
                            }
                          >
                            {t("adminMasterData.deactivate")}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() =>
                              setPending({ action: "activate", row })
                            }
                          >
                            {t("adminMasterData.reactivate")}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <EditDialog
        key={dialog?.mode === "edit" ? dialog.row.id : dialog?.mode ?? "closed"}
        kind={kind}
        state={dialog}
        fields={fields}
        toFormValues={toFormValues}
        buildBody={buildBody}
        onClose={() => setDialog(null)}
      />
      <ToggleDialog
        key={pending ? `${pending.action}:${pending.row.id}` : "closed"}
        kind={kind}
        pending={pending}
        onClose={() => setPending(null)}
      />
    </div>
  );
}

/**
 * A header that sorts its column (AC-071); same pattern as the incident list.
 * Only the active tab's table is mounted, so the column key alone is unique.
 */
function SortableHead<Row>({
  column,
  sort,
  onSort,
}: {
  column: Column<Row>;
  sort: MasterDataSort | null;
  onSort: (next: MasterDataSort) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const label = t(column.headerKey);
  const state: "ascending" | "descending" | "none" =
    sort?.key !== column.key ? "none" : sort.dir === "asc" ? "ascending" : "descending";
  const Icon = state === "ascending" ? ArrowUp : state === "descending" ? ArrowDown : ArrowUpDown;
  return (
    <TableHead aria-sort={state} className={column.className}>
      <button
        type="button"
        data-testid={`admin-master-data_sort_${column.key}`}
        onClick={() => onSort(nextMasterDataSort(sort, column.key, column.descFirst ?? false))}
        aria-label={t("list.sortBy", { column: label })}
        className="-ml-2 inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-muted hover:text-foreground"
      >
        {label}
        <Icon
          aria-hidden="true"
          className={cn("h-3.5 w-3.5", state === "none" && "text-muted-foreground/50")}
        />
      </button>
    </TableHead>
  );
}

function StatusBadge({ active }: { active: boolean }): JSX.Element {
  const { t } = useTranslation();
  return active ? (
    <Badge
      variant="outline"
      className="whitespace-nowrap border-emerald-300 dark:border-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
    >
      {t("adminMasterData.statusActive")}
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="whitespace-nowrap border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300"
    >
      {t("adminMasterData.statusInactive")}
    </Badge>
  );
}

/* ---------------------------------------------------------------------------
 * Create / edit dialog
 * ------------------------------------------------------------------------- */

function blankValues(fields: FieldSpec[]): MasterDataFormValues {
  return Object.fromEntries(fields.map((field) => [field.name, ""]));
}

function EditDialog<Row extends MasterDataRow>({
  kind,
  state,
  fields,
  toFormValues,
  buildBody,
  onClose,
}: {
  kind: MasterDataKind;
  state: DialogState<Row>;
  fields: FieldSpec[];
  toFormValues: (row: Row) => MasterDataFormValues;
  buildBody: (values: MasterDataFormValues) => MasterDataBody;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorMessages = useMasterDataErrors();
  const create = useCreateMasterData(kind);
  const update = useUpdateMasterData(kind);

  const isEdit = state?.mode === "edit";
  // The dialog is keyed on its target and remounts when it changes, so the
  // initial values are computed exactly once per open (lazy initializer).
  const [values, setValues] = useState<MasterDataFormValues>(() =>
    state?.mode === "edit"
      ? { ...blankValues(fields), ...toFormValues(state.row) }
      : blankValues(fields),
  );

  const setField = (name: string, value: string): void =>
    setValues((current) => ({ ...current, [name]: value }));

  const nameEmpty = (values.name ?? "").trim() === "";
  const isPending = create.isPending || update.isPending;

  const onSubmit = (): void => {
    if (nameEmpty) return;
    const body = buildBody(values);
    const callbacks = {
      onSuccess: () => {
        onClose();
        toast.success(
          isEdit
            ? t("adminMasterData.updated")
            : t("adminMasterData.created"),
        );
      },
      onError: (error: unknown) => notifyApiError(error, errorMessages),
    };
    if (state?.mode === "edit") {
      update.mutate({ id: state.row.id, body }, callbacks);
    } else {
      create.mutate(body, callbacks);
    }
  };

  return (
    <Dialog
      open={state !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("adminMasterData.editTitle")
              : t("adminMasterData.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("adminMasterData.dialogHint")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {fields.map((field) => (
            <FormField
              key={field.name}
              field={field}
              value={values[field.name] ?? ""}
              onChange={(value) => setField(field.name, value)}
            />
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            className="h-11 sm:h-9"
            disabled={nameEmpty || isPending}
            onClick={onSubmit}
          >
            {isPending
              ? t("adminMasterData.saving")
              : t("adminMasterData.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  field,
  value,
  onChange,
}: {
  field: FieldSpec;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const label = t(field.labelKey);
  const placeholder = field.placeholderKey ? t(field.placeholderKey) : undefined;

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium" htmlFor={`md-${field.name}`}>
        {label}
        {field.name === "name" ? (
          <span aria-hidden="true" className="text-destructive-text">
            {" "}
            *
          </span>
        ) : null}
      </label>
      {field.type === "textarea" ? (
        <Textarea
          id={`md-${field.name}`}
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : field.type === "select" ? (
        <Select
          value={value === "" ? NONE : value}
          onValueChange={(next) => onChange(next === NONE ? "" : next)}
        >
          <SelectTrigger id={`md-${field.name}`} aria-label={label}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t("adminMasterData.optionNone")}</SelectItem>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === "color" ? (
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block h-6 w-6 shrink-0 rounded-full border ring-1 ring-black/10"
            style={value.trim() !== "" ? { backgroundColor: value } : undefined}
          />
          <Input
            id={`md-${field.name}`}
            value={value}
            placeholder={placeholder ?? "#RRGGBB"}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      ) : (
        <Input
          id={`md-${field.name}`}
          type={field.type === "number" ? "number" : "text"}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Deactivate / reactivate confirmation (destructive pattern, IM-150)
 * ------------------------------------------------------------------------- */

function ToggleDialog<Row extends MasterDataRow>({
  kind,
  pending,
  onClose,
}: {
  kind: MasterDataKind;
  pending: PendingToggle<Row>;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorMessages = useMasterDataErrors();
  const deactivate = useDeactivateMasterData(kind);
  const activate = useActivateMasterData(kind);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isDeactivate = pending?.action === "deactivate";
  const isPending = deactivate.isPending || activate.isPending;

  const onConfirm = (): void => {
    if (!pending) return;
    const callbacks = {
      onSuccess: () => {
        onClose();
        toast.success(
          pending.action === "deactivate"
            ? t("adminMasterData.deactivated")
            : t("adminMasterData.reactivated"),
        );
      },
      onError: (error: unknown) => {
        onClose();
        notifyApiError(error, errorMessages);
      },
    };
    if (pending.action === "deactivate") {
      deactivate.mutate(pending.row.id, callbacks);
    } else {
      activate.mutate(pending.row.id, callbacks);
    }
  };

  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        onOpenAutoFocus={(event) => {
          if (!isDeactivate) return;
          event.preventDefault();
          cancelRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isDeactivate
              ? t("adminMasterData.deactivateDialog.title")
              : t("adminMasterData.reactivateDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {isDeactivate
              ? t("adminMasterData.deactivateDialog.question", {
                  name: pending?.row.name ?? "",
                })
              : t("adminMasterData.reactivateDialog.question", {
                  name: pending?.row.name ?? "",
                })}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm">
          {isDeactivate
            ? t("adminMasterData.deactivateDialog.consequence")
            : t("adminMasterData.reactivateDialog.consequence")}
        </p>
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant={isDeactivate ? "destructive" : "default"}
            className="h-11 sm:h-9"
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending
              ? t("adminMasterData.saving")
              : isDeactivate
                ? t("adminMasterData.deactivate")
                : t("adminMasterData.reactivate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
